import { TrashIcon, ArrowTopRightOnSquareIcon, CreditCardIcon, DocumentTextIcon } from "@heroicons/react/24/outline"
import Badge from "aldoo-ra/Badge"
import Button from "aldoo-ra/Button"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import DataTable from "aldoo-ra/DataTable"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Modal from "aldoo-ra/Modal"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Tabs from "aldoo-ra/Tabs"
import Tooltip from "aldoo-ra/Tooltip"
import Typography from "aldoo-ra/Typography"
import { url } from "aldoo-ra/CMS/url"
import moment from "moment"
import { useState } from "react"

// Import APIs
import { api } from "aldoo-ra/API"
import UserPlatformAPI from "aldoo-ra/Admin/PlatformAccess/user-api"
import StoreProductInfoDialog from "aldoo-ra/Admin/Store/Products/StoreProductInfoDialog"
import UserInfoDrawer from "aldoo-ra/Admin/PlatformAccess/UserInfoDrawer"
import { getPlatformDisplayName, renderPlatformIcon } from "aldoo-ra/Admin/utils/platformIconUtils.jsx"
import PlatformSelector from "aldoo-ra/PlatformSelector"
import storeTransactionCRUD, { StoreTransactionAPI } from "./api"

export default function TransactionBrowser() {
  const { getType } = DatabaseModelTypeResolver()
  const StoreProduct = getType("StoreProduct")

  const [refreshKey, setRefreshKey] = useState(0)
  const [platformFilter, setPlatformFilter] = useState([])
  const [typeFilter, setTypeFilter] = useState([])
  const [userFilter, setUserFilter] = useState(null)
  const [dateFilter, setDateFilter] = useState({ type: "preset", value: "lifetime" })
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedProductPid, setSelectedProductPid] = useState(null)
  const [showProductInfoDialog, setShowProductInfoDialog] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserInfoDrawer, setShowUserInfoDrawer] = useState(false)
  const [activeTab, setActiveTab] = useState("overview")

  // Filter helpers
  const getPlatformFilter = () => {
    if (!platformFilter || !platformFilter.length) return { $exists: true }
    return { $in: platformFilter.map((t) => t.value) }
  }

  const getTypeFilter = () => {
    if (!typeFilter || !typeFilter.length) return { $exists: true }

    // Check if "renewal" or "trial_conversion" is in the filter
    const hasRenewal = typeFilter.some(t => t.value === 'renewal')
    const hasTrialConversion = typeFilter.some(t => t.value === 'trial_conversion')
    const otherTypes = typeFilter.filter(t => t.value !== 'renewal' && t.value !== 'trial_conversion').map(t => t.value)

    // If only renewal is selected, filter by is_renew: true
    if (hasRenewal && !hasTrialConversion && otherTypes.length === 0) {
      return 'subscription' // Will be combined with is_renew filter below
    }

    // If only trial conversion is selected, filter by trial_converted: true
    if (hasTrialConversion && !hasRenewal && otherTypes.length === 0) {
      return 'subscription' // Will be combined with trial_converted filter below
    }

    // If both renewal and trial conversion are selected (without other types)
    if (hasRenewal && hasTrialConversion && otherTypes.length === 0) {
      return 'subscription' // Will be combined with $or filter below
    }

    // If renewal/trial_conversion and other types are selected, include subscription type
    // (renewal and trial_conversion transactions have type: 'subscription')
    if (hasRenewal || hasTrialConversion) {
      return { $in: [...otherTypes, 'subscription'] }
    }

    return { $in: otherTypes }
  }

  const getUserFilter = () => {
    if (!userFilter || !userFilter.length) return { $exists: true }
    const selectedUser = userFilter[0]
    return selectedUser._id
  }

  const getDateFilter = () => {
    if (!dateFilter) {
      return { $exists: true }
    }

    // Handle standard CMS date filter structure
    if (dateFilter.type === "preset") {
      if (!dateFilter.value || dateFilter.value === null) {
        return { $exists: true }
      }

      // Handle preset with days value
      if (dateFilter.value.days) {
        const cutoffDate = moment().subtract(dateFilter.value.days, "days").utc().toISOString()
        return { $gte: cutoffDate }
      }
    }

    // Handle custom date range
    if (dateFilter.type === "custom" && dateFilter.range) {
      const { startDate, endDate } = dateFilter.range
      if (startDate && endDate) {
        const adjustedStartDate = moment(startDate).utc().startOf("day").toISOString()
        const adjustedEndDate = moment(endDate).utc().endOf("day").toISOString()

        return {
          $gte: adjustedStartDate,
          $lte: adjustedEndDate
        }
      }
    }

    return { $exists: true }
  }

  // Custom API method that handles filtering and product lookup
  const customGetMethod = async (requestParams) => {
    // Convert DataTable sort { field, order } → MongoDB sort or client-side
    let sort = requestParams.sort
    let clientSideSort = null

    if (sort?.field) {
      const fieldMap = {
        price_snapshot: "price_snapshot.amount",
        user: null, // populated ref — client-side
      }
      const mapped = fieldMap.hasOwnProperty(sort.field) ? fieldMap[sort.field] : sort.field
      const dir = sort.order === "asc" ? 1 : -1

      if (mapped === null) {
        clientSideSort = { field: sort.field, dir }
        sort = { created: -1 }
      } else {
        sort = { [mapped]: dir }
      }
    }

    // Check if renewal/trial_conversion filters are active
    const hasRenewal = typeFilter?.some(t => t.value === 'renewal')
    const hasTrialConversion = typeFilter?.some(t => t.value === 'trial_conversion')
    const hasSubscription = typeFilter?.some(t => t.value === 'subscription')
    const otherTypes = typeFilter?.filter(t => t.value !== 'renewal' && t.value !== 'trial_conversion').map(t => t.value) || []
    const onlyRenewal = hasRenewal && !hasTrialConversion && otherTypes.length === 0
    const onlyTrialConversion = hasTrialConversion && !hasRenewal && otherTypes.length === 0
    const onlyRenewalOrTrialConversion = (hasRenewal || hasTrialConversion) && otherTypes.length === 0

    const finalParams = {
      ...requestParams,
      sort,
      populate: ["user"],
      match: {
        ...requestParams.match,
        platform: getPlatformFilter(),
        type: getTypeFilter(),
        user: getUserFilter(),
        created: getDateFilter(),
        // Add is_renew/trial_converted filters
        // - If only "renewal" selected: is_renew = true
        // - If only "trial_conversion" selected: trial_converted = true
        // - If both "renewal" and "trial_conversion" selected: is_renew = true OR trial_converted = true
        // - If only "subscription" selected: is_renew = false or not exists AND trial_converted = false or not exists
        // - If "subscription" with "renewal" and/or "trial_conversion": no additional filters
        ...(onlyRenewal && { is_renew: true }),
        ...(onlyTrialConversion && { trial_converted: true }),
        ...(hasRenewal && hasTrialConversion && otherTypes.length === 0 && {
          $or: [{ is_renew: true }, { trial_converted: true }]
        }),
        ...(hasSubscription && !hasRenewal && !hasTrialConversion && {
          $or: [
            { is_renew: false, trial_converted: false },
            { is_renew: { $exists: false }, trial_converted: false },
            { is_renew: false, trial_converted: { $exists: false } },
            { is_renew: { $exists: false }, trial_converted: { $exists: false } }
          ]
        })
      }
    }

    const response = await StoreTransactionAPI.GetTransactions(finalParams)

    // Manually lookup products for each transaction
    if (response.result && response.data && response.data.length > 0) {
      // Get unique product IDs
      const productIds = [...new Set(response.data.map(t => t.product_id).filter(Boolean))]

      if (productIds.length > 0) {
        try {
          // Fetch all products in one query
          const crudAPI = api("crud")
          const productsResponse = await crudAPI({
            get: {
              model: "StoreProduct",
              match: { pid: { $in: productIds } }
            }
          })

          // Create a map of pid -> product
          const productMap = {}
          if (Array.isArray(productsResponse)) {
            productsResponse.forEach(product => {
              productMap[product.pid] = product
            })
          }

          // Attach products to transactions
          response.data = response.data.map(transaction => ({
            ...transaction,
            product: productMap[transaction.product_id] || null
          }))
        } catch (error) {
          console.error("Failed to fetch products:", error)
        }
      }
    }

    if (clientSideSort && response?.data?.length) {
      response.data.sort((a, b) => {
        const aVal = (a[clientSideSort.field]?.name || a[clientSideSort.field]?.email || "").toLowerCase()
        const bVal = (b[clientSideSort.field]?.name || b[clientSideSort.field]?.email || "").toLowerCase()
        return aVal.localeCompare(bVal) * clientSideSort.dir
      })
    }

    return response
  }

  // Handle product click - open read-only info dialog (or update if already open)
  const handleProductClick = (productId) => {
    if (!productId) return
    setSelectedProductPid(productId)
    if (!showProductInfoDialog) {
      setShowProductInfoDialog(true)
    }
  }

  // Handle user click - open user info drawer
  const handleUserClick = (user) => {
    if (!user) return
    setSelectedUser(user)
    if (!showUserInfoDrawer) {
      setShowUserInfoDrawer(true)
    }
  }

  // Utility functions
  const formatDate = (date) => {
    if (!date) return { date: "N/A", timeAgo: "" }
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      const now = moment()
      const transactionDate = moment(dateObj)
      const timeAgo = transactionDate.from(now)
      const formattedDate = transactionDate.format('MMM DD, YYYY HH:mm')
      return { date: formattedDate, timeAgo }
    } catch (error) {
      return { date: "Invalid date", timeAgo: "" }
    }
  }

  const formatPrice = (priceSnapshot) => {
    if (!priceSnapshot) return "N/A"
    const { amount, currency, final_amount, promo_code } = priceSnapshot
    if (!currency) return "N/A"

    // Use final_amount if promo code was applied, otherwise use amount
    const displayValue = promo_code && final_amount != null ? final_amount : amount
    if (!displayValue && displayValue !== 0) return "N/A"

    // Convert cents to dollars if needed
    const displayAmount = displayValue > 999 ? (displayValue / 100).toFixed(2) : displayValue.toFixed(2)
    return `${currency.toUpperCase()} ${displayAmount}`
  }

  const getTransactionTypeBadgeVariant = (type) => {
    // Normalize the type (trim whitespace and lowercase)
    const normalizedType = type?.toString().trim().toLowerCase()

    switch (normalizedType) {
      case "purchase": return "admin_success"        // Green - one-time purchases
      case "subscription": return "admin_primary"    // Blue - recurring subscriptions
      case "license-key": return "admin_warning"     // Orange - license activations
      case "trial": return "admin_info"              // Light blue - trial periods
      case "trial conversion": return "admin_success" // Green - trial converted to paid
      case "renewal": return "admin_primary"         // Blue - subscription renewals
      case "refund": return "admin_error"           // Red - refunds/cancellations
      default: return "admin_default"                // Gray - unknown types
    }
  }

  // Function to handle opening details modal
  const openDetailsModal = (transaction) => {
    setSelectedTransaction(transaction)
    setShowDetailsModal(true)
  }

  // Function to handle deleting a transaction
  const handleDeleteTransaction = () => {
    if (!selectedTransaction) return

    showPromptDialog({
      title: "Delete Transaction",
      message: `Are you sure you want to delete this transaction?`,
      details: `Transaction ID: ${selectedTransaction._id}`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          // Use CRUD hard delete method - pass the whole transaction object
          await storeTransactionCRUD.HardDelete(selectedTransaction)

          // Close modal and refresh data
          setShowDetailsModal(false)
          setSelectedTransaction(null)
          setRefreshKey((prev) => prev + 1)

          // Optional: Show success notification
          // showNotification("Transaction deleted successfully", "success")
        } catch (error) {
          console.error("Failed to delete transaction:", error)
          // Optional: Show error notification
          // showNotification("Failed to delete transaction", "error")
        }
      },
      onCancel: () => {
        // Do nothing - keep modal open
      }
    })
  }

  // Helper function to render a field row
  const renderField = (key, value) => (
    <div className="flex justify-between items-start gap-4 py-2 border-b border-admin_bordered last:border-b-0">
      <Typography
        variant="admin_txt1_accent"
        className="text-admin_text min-w-[120px] flex-shrink-0"
      >
        {key}
      </Typography>

      <div className="break-all text-right flex-1">
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography variant="admin_txt1_dim">
            {value || "N/A"}
          </Typography>
        ) : (
          value || <Typography variant="admin_txt1_dim">N/A</Typography>
        )}
      </div>
    </div>
  )

  // Helper to render clickable chip
  const renderChip = (label, onClick, icon = null) => (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-admin_primary/10 hover:bg-admin_primary/20 text-admin_primary rounded-full text-sm font-medium transition-colors"
    >
      {icon}
      <span>{label}</span>
      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
    </button>
  )

  // Helper to format payment method
  const formatPaymentMethod = (paymentMethod) => {
    if (!paymentMethod || typeof paymentMethod !== 'object') return "N/A"

    const { type, brand, last4 } = paymentMethod

    if (type === 'card' && brand && last4) {
      const brandName = brand.charAt(0).toUpperCase() + brand.slice(1)
      return (
        <div className="flex items-center gap-2 justify-end">
          <CreditCardIcon className="w-5 h-5 text-admin_text" />
          <span className="font-medium">{brandName}</span>
          <span className="text-admin_text_secondary">••••</span>
          <span className="font-mono">{last4}</span>
        </div>
      )
    }

    return type || "N/A"
  }

  // Function to render transaction overview tab (combined details & payment)
  const renderOverviewTab = (transaction) => {
    if (!transaction) return null

    const metadata = transaction.metadata || {}
    const discount = metadata.discount || {}

    return (
      <div className="space-y-4">
        {/* Transaction Info Section */}
        <div className="space-y-3">
          {renderField("ID", transaction._id)}
          {renderField("Type", (() => {
            let displayType = transaction.type
            if (transaction.is_refund || transaction.is_reversal) {
              displayType = 'refund'
            } else if (transaction.trial_converted) {
              displayType = 'trial conversion'
            } else if (transaction.is_renew) {
              displayType = 'renewal'
            } else if (transaction.trial_started) {
              displayType = 'trial'
            }
            return (
              <Badge variant={getTransactionTypeBadgeVariant(displayType)}>
                {displayType}
              </Badge>
            )
          })())}
          {renderField("Receipt ID", transaction.receipt_id)}
          {renderField("Created", formatDate(transaction.created).date)}
          {formatDate(transaction.created).timeAgo &&
            renderField("Time Ago", formatDate(transaction.created).timeAgo)
          }
        </div>

        {/* Product Section */}
        <div className="space-y-3 pt-2 border-t border-admin_bordered">
          {renderField("Product",
            renderChip(
              transaction.product?.name || transaction.product_id,
              () => handleProductClick(transaction.product?.pid || transaction.product_id)
            )
          )}
        </div>

        {/* User Section */}
        {transaction.user && (
          <div className="space-y-3 pt-2 border-t border-admin_bordered">
            {renderField("User",
              renderChip(
                `${transaction.user.name || "Unknown User"} (${transaction.user.email || "No email"})`,
                () => handleUserClick(transaction.user)
              )
            )}
          </div>
        )}

        {/* Pricing Section */}
        <div className="bg-admin_canvas rounded-lg p-4 space-y-3">
          <Typography 
            variant="admin_label" 
            className="text-admin_text mb-2"
          >
            Pricing Details
          </Typography>

          {transaction.price_snapshot?.promo_code ? (
            <>
              {renderField("Original Price",
                transaction.price_snapshot.original_amount
                  ? `${transaction.price_snapshot.currency?.toUpperCase()} ${(transaction.price_snapshot.original_amount / 100).toFixed(2)}`
                  : "N/A"
              )}

              {/* Discount Panel */}
              <div className="bg-admin_success/10 border border-admin-success/30 rounded-lg p-3 my-2">
                <div className="flex items-center justify-between mb-2">
                  <Typography 
                    variant="admin_txt1_accent" 
                    className="text-admin_success"
                  >
                    Discount Applied
                  </Typography>

                  <Badge variant="admin_success" className="text-xs">
                    {transaction.price_snapshot.promo_code}
                  </Badge>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-admin_success/80">
                      Discount Type:
                    </span>

                    <span className="text-admin_success font-medium">
                      {discount.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-admin_success/80">
                      Amount:
                    </span>

                    <span className="text-admin_success font-medium">
                      -{transaction.price_snapshot.discount_amount
                        ? `${transaction.price_snapshot.currency?.toUpperCase()} ${(transaction.price_snapshot.discount_amount / 100).toFixed(2)}`
                        : "N/A"
                      }
                      {discount.type === 'percentage' && ` (${discount.percentOff || discount.amount || 0}%)`}
                    </span>
                  </div>

                  {discount.timesRedeemed && (
                    <div className="flex justify-between text-sm">
                      <span className="text-admin_success/80">
                        Times Redeemed:
                      </span>

                      <span className="text-admin_success">
                        {discount.timesRedeemed}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {renderField("Final Price", (
                <span className="text-lg font-bold text-admin_success">
                  {formatPrice(transaction.price_snapshot)}
                </span>
              ))}
            </>
          ) : (
            renderField("Price", (
              <span className="text-lg font-bold text-admin_text">
                {formatPrice(transaction.price_snapshot)}
              </span>
            ))
          )}

          {transaction.price_snapshot?.duration > 0 &&
            renderField("Duration", `${transaction.price_snapshot.duration} days`)
          }
        </div>

        {/* Payment Method Section */}
        <div className="space-y-3">
          {metadata.payment_method && renderField("Payment Method",
            formatPaymentMethod(metadata.payment_method)
          )}

          {renderField("Platform", (
            <div className="flex items-center gap-2 justify-end">
              {renderPlatformIcon(transaction.platform, "w-5 h-5 text-admin_neutral_3")}

              <span className="text-admin_text_secondary">
                {getPlatformDisplayName(transaction.platform)}
              </span>
            </div>
          ))}

          {renderField("Currency", transaction.price_snapshot?.currency?.toUpperCase() || metadata.currency?.toUpperCase() || "N/A")}

          {/* Stripe Receipt Links */}
          {metadata.receipt_url && renderField("PDF Receipt",
            renderChip(
              "View Receipt",
              () => window.open(metadata.receipt_url, '_blank'),
              <DocumentTextIcon className="w-4 h-4" />
            )
          )}

          {metadata.hosted_invoice_url && renderField("Invoice",
            renderChip(
              "View Invoice",
              () => window.open(metadata.hosted_invoice_url, '_blank'),
              <DocumentTextIcon className="w-4 h-4" />
            )
          )}

          {(metadata.stripe_payment_intent || metadata.purchase_id) &&
            renderField("Payment Intent",
              <code className="text-xs bg-admin_neutral_1 px-2 py-1 rounded">
                {metadata.stripe_payment_intent || metadata.purchase_id}
              </code>
            )
          }
        </div>
      </div>
    )
  }


  // Function to render metadata tab
  const renderMetadataTab = (transaction) => {
    if (!transaction) return null

    const metadataEntries = transaction.metadata && typeof transaction.metadata === 'object'
      ? Object.entries(transaction.metadata).filter(([key, value]) => value !== null && value !== undefined)
      : []

    if (metadataEntries.length === 0) {
      return (
        <div className="text-center py-8">
          <Typography variant="admin_txt1_dim">
            No metadata available
          </Typography>
        </div>
      )
    }

    return (
      <div className="space-y-3">
        {metadataEntries.map(([key, value]) => (
          <div key={key} className="py-2 border-b border-admin_bordered last:border-b-0">
            <Typography 
              variant="admin_txt1_accent" 
              className="text-admin_text mb-1"
            >
              {key}
            </Typography>

            <div className="ml-4">
              {typeof value === 'object' ? (
                <pre className="whitespace-pre-wrap font-mono text-xs bg-admin_neutral_1 p-3 rounded text-admin_text">
                  {JSON.stringify(value, null, 2)}
                </pre>
              ) : (
                <Typography 
                  variant="admin_txt1_dim" 
                  className="break-all"
                >
                  {String(value)}
                </Typography>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Function to render active tab content
  const renderTabContent = (transaction) => {
    if (!transaction) return null

    switch (activeTab) {
      case "overview":
        return renderOverviewTab(transaction)
      case "metadata":
        return renderMetadataTab(transaction)
      default:
        return null
    }
  }


  const transactionItemRender = [
    {
      id: "price_snapshot",
      title: "Price",
      sortable: true,
      headerClassName: "w-[140px]",
      render: (value, item) => {
        const isRefund = item.type?.toString().trim().toLowerCase() === "refund" ||
                        (item.price_snapshot?.amount && item.price_snapshot.amount < 0)
        const hasPromoCode = item.price_snapshot?.promo_code

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Typography
                variant="admin_label"
                className={`my-0 ${isRefund ? 'text-admin_error' : ''}`}
              >
                {formatPrice(item.price_snapshot)}
              </Typography>

              {hasPromoCode && (
                <Badge variant="admin_success" className="text-[10px] px-1.5 py-0.5">
                  {item.price_snapshot.promo_code}
                </Badge>
              )}
            </div>

            {item.price_snapshot?.duration > 0 && (
              <Typography
                variant="admin_txt1_dim"
                className="my-0"
              >
                {item.price_snapshot.duration} days
              </Typography>
            )}
          </div>
        )
      },
    },
    {
      id: "product_id",
      title: "Product",
      sortable: true,
      headerClassName: "w-[200px]",
      render: (value, item) => {
        const productName = item.product?.name || item.product_id
        const productPid = item.product?.pid || item.product_id

        return (
          <button
            onClick={() => handleProductClick(productPid)}
            className="font-medium text-admin_primary hover:text-admin_primary_lt hover:underline cursor-pointer text-left"
          >
            {productName}
          </button>
        )
      },
    },
    {
      id: "type",
      title: "Type",
      sortable: true,
      headerClassName: "w-[100px]",
      render: (value, item) => {
        // Determine display type based on flags
        let displayType = item.type
        if (item.is_refund || item.is_reversal) {
          displayType = 'refund'
        } else if (item.trial_converted) {
          displayType = 'trial conversion'
        } else if (item.is_renew) {
          displayType = 'renewal'
        } else if (item.trial_started) {
          displayType = 'trial'
        }
        return (
          <Badge variant={getTransactionTypeBadgeVariant(displayType)}>
            {displayType}
          </Badge>
        )
      },
    },
    {
      id: "platform",
      title: "Platform",
      sortable: true,
      headerClassName: "w-[80px] text-center",
      render: (value, item) => (
        <Tooltip
          content={getPlatformDisplayName(item.platform)}
          display="block"
        >
          <div className="flex items-center justify-center">
            {renderPlatformIcon(item.platform, "w-6 h-6 text-admin_text_secondary")}
          </div>
        </Tooltip>
      ),
    },
    {
      id: "user",
      title: "User",
      sortable: true,
      headerClassName: "w-[180px]",
      render: (value, item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
            {item.user?.avatar ? (
              <img src={url(item.user.avatar)} alt={item.user.name} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <span className="text-white font-medium text-sm">
                {(item.user?.name || item.user?.email || 'U').charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          <div className="flex flex-col min-w-0">
            <Typography
              variant="admin_label"
              className="truncate my-0"
            >
              {item.user?.name || "Unknown User"}
            </Typography>

            <Typography
              variant="admin_txt1_dim"
              className="truncate"
            >
              {item.user?.email || "No email"}
            </Typography>
          </div>
        </div>
      ),
    },
    {
      id: "created",
      title: "Date",
      sortable: true,
      headerClassName: "w-[130px]",
      render: (value, item) => {
        const dateInfo = formatDate(item.created)
        return (
          <div className="flex flex-col">
            <Typography
              variant="admin_txt1" >
              {dateInfo.date}
            </Typography>

            {dateInfo.timeAgo && (
              <Typography
                variant="admin_txt2_dim"
                className="italic"
              >
                {dateInfo.timeAgo}
              </Typography>
            )}
          </div>
        )
      },
    },
  ]

  const headerControls = (
    <div className="w-full flex items-center justify-between -mt-4 mb-1">
      {/* Date Range Filter */}
      <DateFilterDropdown
        variant="admin"
        showLabel={false}
        value={dateFilter}
        onChange={(value) => {
          setDateFilter(value)
          setRefreshKey((prev) => prev + 1)
        }}
        className="w-[160px]"
        styleOverrides={{
          popup: "absolute right-0 text-admin_text bg-admin_paper border border-admin_bordered rounded-lg shadow-lg mt-1 p-2 w-[200px] z-50"
        }}
      />

      {/* Transaction Type Filter */}
      <DropDownSelect
        variant="admin"
        searchEnabled
        options={[
          { value: "purchase", name: "Purchase" },
          { value: "subscription", name: "Subscription" },
          { value: "license-key", name: "License Key" },
          { value: "trial", name: "Trial" },
          { value: "trial_conversion", name: "Trial Conversion" },
          { value: "renewal", name: "Renewal" },
          { value: "refund", name: "Refund" },
        ]}
        multiSelection
        fixedWidth="180px"
        placeholder="Filter by type"
        value={typeFilter}
        onChange={(value) => {
          setTypeFilter(value)
          setRefreshKey((prev) => prev + 1)
        }}
      />

      {/* Platform Filter */}
      <PlatformSelector
        platforms={[
          { value: "Web", label: "Web" },
          { value: "iOS", label: "iOS" },
          { value: "Android", label: "Android" },
          { value: "Windows", label: "Windows" },
          { value: "Mac", label: "Mac" },
        ]}
        fixedWidth="180px"
        placeholder="Platform"
        value={platformFilter}
        onChange={(value) => {
          setPlatformFilter(value)
          setRefreshKey((prev) => prev + 1)
        }}
      />

      {/* User Filter */}
      <DropDownSelect
        variant="admin"
        searchEnabled
        useExternalSearch
        enableResetButton
        showCheckbox={false}
        apiMethod={async (params) => {
          if (!params.searchTerm || params.searchTerm.length < 2) {
            return { data: [] }
          }

          const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
          return response
        }}
        onDataLoaded={(rawData) => {
          return rawData.map(user => ({
            _id: user._id,
            name: user.name || 'Unknown User',
            email: user.email || 'No email',
            value: user._id,
            label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
            ...user
          }))
        }}
        searchField="name email"
        valueField="_id"
        nameField="label"
        fixedWidth="300px"
        placeholder="Filter by user"
        noDataMessage="Filter by user"
        value={userFilter}
        onChange={(value) => {
          setUserFilter(value)
          setRefreshKey((prev) => prev + 1)
        }}
        onValueItemRender={(user) => {
          if (!user) return "Filter by user"
          return (
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                {user.avatar ? (
                  <img src={url(user.avatar)} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-medium text-xs">
                    {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-admin_text truncate">
                  {user.name || 'Unknown User'}
                </div>

                <div className="text-xs text-admin_text_secondary truncate">
                  {user.email || 'No email'}
                </div>
              </div>
            </div>
          )
        }}
        onItemRender={(user) => (
          <div className="flex items-center gap-3 p-2">
            <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
              {user.avatar ? (
                <img src={url(user.avatar)} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <span className="text-white font-medium text-sm">
                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                </span>
              )}
            </div>

            <div className="flex-1">
              <div className="font-medium text-sm text-admin_text">
                {user.name || 'Unknown User'}
              </div>

              <div className="text-xs text-admin_text_secondary">
                {user.email || 'No email'}
              </div>
            </div>
          </div>
        )}
      />
    </div>
  )

  return (
    <div className="p-4">
      <Typography
        component="h1"
        variant="admin_h3"
        className="ml-4  mb-5"
      >
        Store Transactions
      </Typography>

      <DataTable
        variant="admin_browser"
        preferredHeight="70vh"
        apiMethod={customGetMethod}
        headerControls={headerControls}
        itemHeight="h-[70px]"
        searchField="product_id receipt_id type platform"
        searchObject={{}}
        requestFields={{}}
        sort={{ created: -1 }}
        renders={transactionItemRender}
        refreshKey={refreshKey}
        localUserPreferencesKey="store_transactions"
        styleOverrides={{
          table: "w-full table-fixed",
          searchInput: "absolute -top-16 right-0 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1"
        }}
        noData={
          <Typography variant="admin_body">
            No transactions matching the search criteria
          </Typography>
        }
      />

      {/* Transaction Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false)
          setSelectedTransaction(null)
          setActiveTab("overview")
        }}
        title="Transaction Details"
        variant="admin"
        className="!max-w-3xl"
        closeOnEscape={true}
        closeOnClickOutside={false}
        styleOverrides={{
          closeButton: "absolute right-2 top-6 hover:text-white z-10",
          header: "relative flex justify-between items-center px-4 pt-4 pb-2",
          content: "flex-1 flex flex-col overflow-hidden p-0"
        }}
        headerContent={
          <Button
            variant="admin_error"
            className="px-3 py-1 mr-10"
            onClick={handleDeleteTransaction}
          >
            <TrashIcon className="w-4 h-4 mr-1" />
            Delete
          </Button>
        }
      >
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
          <div className="px-6 pt-2 pb-6">
            {/* Tabs */}
            <Tabs
              variant="admin"
              tabs={[
                { label: "Overview", value: "overview" },
                { label: "Metadata", value: "metadata" },
              ]}
              value={activeTab}
              onChange={setActiveTab}
              className="mb-4"
            />

            {/* Tab Content */}
            <div className="py-4 min-h-[300px]">
              {renderTabContent(selectedTransaction)}
            </div>
          </div>
        </div>
      </Modal>

      {/* Product Info Dialog */}
      <StoreProductInfoDialog
        isOpen={showProductInfoDialog}
        onClose={() => {
          setShowProductInfoDialog(false)
          setSelectedProductPid(null)
        }}
        pid={selectedProductPid}
      />

      {/* User Info Drawer */}
      <UserInfoDrawer
        isOpen={showUserInfoDrawer}
        onClose={() => {
          setShowUserInfoDrawer(false)
          setSelectedUser(null)
        }}
        userObject={selectedUser}
      />
    </div>
  )
}
